import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Phone, Check, Info, Plus, Trash2, Star, Copy, ExternalLink } from "lucide-react";
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/shared/PageHeader";
import { PullToRefresh } from "@/components/shared/PullToRefresh";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";

interface WhatsAppNumber {
  id: string;
  number: string;
  label: string;
  isPrimary: boolean;
}

export default function WhatsAppSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [whatsappNumbers, setWhatsappNumbers] = useState<WhatsAppNumber[]>([]);
  const [newNumber, setNewNumber] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [autoReply, setAutoReply] = useState(true);
  const [catalogSync, setCatalogSync] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchWhatsAppSettings();
    }
  }, [user]);

  const fetchWhatsAppSettings = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('whatsapp_number, business_name')
        .eq('id', user?.id)
        .single();

      if (profile) {
        setBusinessName(profile.business_name || "");
        
        // Parse existing whatsapp numbers (stored as JSON or comma-separated)
        if (profile.whatsapp_number) {
          try {
            const numbers = JSON.parse(profile.whatsapp_number);
            if (Array.isArray(numbers)) {
              setWhatsappNumbers(numbers);
            } else {
              // Legacy single number format
              setWhatsappNumbers([{
                id: '1',
                number: profile.whatsapp_number,
                label: 'Primary',
                isPrimary: true
              }]);
            }
          } catch {
            // Legacy single number string
            setWhatsappNumbers([{
              id: '1',
              number: profile.whatsapp_number,
              label: 'Primary',
              isPrimary: true
            }]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching WhatsApp settings:', error);
    }
  }, [user?.id]);

  const saveNumbers = async (numbers: WhatsAppNumber[]) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          whatsapp_number: JSON.stringify(numbers),
          business_name: businessName || user?.email?.split('@')[0]
        })
        .eq('id', user?.id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error saving WhatsApp numbers:', error);
      return false;
    }
  };

  const handleAddNumber = async () => {
    if (!newNumber || newNumber.length < 10) {
      toast.error("Please enter a valid WhatsApp number (10+ digits)");
      return;
    }

    if (whatsappNumbers.length >= 5) {
      toast.error("Maximum 5 WhatsApp numbers allowed");
      return;
    }

    // Check for duplicates
    if (whatsappNumbers.some(n => n.number === newNumber)) {
      toast.error("This number is already added");
      return;
    }

    setLoading(true);
    
    const newNumberObj: WhatsAppNumber = {
      id: Date.now().toString(),
      number: newNumber.replace(/\D/g, ''),
      label: newLabel || `Number ${whatsappNumbers.length + 1}`,
      isPrimary: whatsappNumbers.length === 0
    };

    const updatedNumbers = [...whatsappNumbers, newNumberObj];
    
    if (await saveNumbers(updatedNumbers)) {
      setWhatsappNumbers(updatedNumbers);
      setNewNumber("");
      setNewLabel("");
      toast.success("WhatsApp number added successfully!");
    } else {
      toast.error("Failed to add number");
    }
    
    setLoading(false);
  };

  const handleRemoveNumber = async (id: string) => {
    setLoading(true);
    
    const numberToRemove = whatsappNumbers.find(n => n.id === id);
    let updatedNumbers = whatsappNumbers.filter(n => n.id !== id);
    
    // If removing primary, set first remaining as primary
    if (numberToRemove?.isPrimary && updatedNumbers.length > 0) {
      updatedNumbers[0].isPrimary = true;
    }
    
    if (await saveNumbers(updatedNumbers)) {
      setWhatsappNumbers(updatedNumbers);
      toast.success("Number removed");
    } else {
      toast.error("Failed to remove number");
    }
    
    setLoading(false);
  };

  const handleSetPrimary = async (id: string) => {
    setLoading(true);
    
    const updatedNumbers = whatsappNumbers.map(n => ({
      ...n,
      isPrimary: n.id === id
    }));
    
    if (await saveNumbers(updatedNumbers)) {
      setWhatsappNumbers(updatedNumbers);
      toast.success("Primary number updated");
    } else {
      toast.error("Failed to update");
    }
    
    setLoading(false);
  };

  const handleCopyStorefrontLink = async () => {
    const link = `https://fdcqcibrdsuvmdwagulk.supabase.co/functions/v1/og-storefront?seller=${user?.id}`;
    await navigator.clipboard.writeText(link);
    setCopiedLink(true);
    toast.success("Storefront link copied! Share this on WhatsApp for rich previews");
    setTimeout(() => setCopiedLink(false), 3000);
  };

  const handleShareStorefront = () => {
    const link = `https://sabitradeng.com/sellers/${user?.id}`;
    let message = `${link}\n\n`;
    message += `🛍️ *${businessName || 'My Store'}*\n\n`;
    message += `Browse my products and shop securely!\n\n`;
    message += `#SabiTradeNG #OnlineShopping`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp to share your storefront!');
  };

  const isConnected = whatsappNumbers.length > 0;
  const primaryNumber = whatsappNumbers.find(n => n.isPrimary);

  const { isRefreshing, pullDistance, triggerRefresh } = usePullToRefresh({
    onRefresh: fetchWhatsAppSettings,
  });

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <PullToRefresh pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <div className="container max-w-4xl mx-auto px-4 py-8">
        <PageHeader
          title="WhatsApp Settings"
          description="Manage your WhatsApp numbers for customer communication"
          backTo="/seller/dashboard"
          onRefresh={triggerRefresh}
          isRefreshing={isRefreshing}
        />

        {/* Storefront Sharing Card */}
        <Card className="mb-6 bg-gradient-whatsapp text-whatsapp-foreground">
          <CardHeader>
            <div className="flex items-center gap-2">
              <WhatsAppIcon className="h-6 w-6" size={24} />
              <CardTitle className="text-whatsapp-foreground">Share Your Storefront</CardTitle>
            </div>
            <CardDescription className="text-whatsapp-foreground/80">
              This link shows a rich preview with your product image when shared on WhatsApp
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-white/20 rounded-lg p-3 mb-4">
              <p className="text-xs text-whatsapp-foreground/70 mb-1">Your storefront link with preview:</p>
              <code className="text-sm break-all">
                {`og-storefront?seller=${user?.id?.slice(0, 8)}...`}
              </code>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                variant="glass" 
                className="flex-1"
                onClick={handleShareStorefront}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Share on WhatsApp
              </Button>
              <Button 
                variant="glass" 
                className="flex-1"
                onClick={handleCopyStorefrontLink}
              >
                {copiedLink ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Link
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp Numbers Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Phone className="h-6 w-6 text-green-600" />
                <div>
                  <CardTitle>WhatsApp Numbers</CardTitle>
                  <CardDescription>Add up to 5 WhatsApp numbers for customer contact</CardDescription>
                </div>
              </div>
              <Badge className={isConnected ? "bg-green-500 text-white" : ""} variant={isConnected ? "default" : "secondary"}>
                {whatsappNumbers.length} Number{whatsappNumbers.length !== 1 ? 's' : ''}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add New Number Form */}
            <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
              <Label>Add New WhatsApp Number</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex gap-2 flex-1">
                  <div className="flex items-center px-3 bg-muted rounded-md border">
                    <span className="text-sm">+234</span>
                  </div>
                  <Input
                    placeholder="8012345678"
                    value={newNumber}
                    onChange={(e) => setNewNumber(e.target.value.replace(/\D/g, ''))}
                    className="flex-1"
                    maxLength={15}
                  />
                </div>
                <Input
                  placeholder="Label (e.g., Sales, Support)"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  className="sm:w-40"
                />
                <Button 
                  onClick={handleAddNumber} 
                  disabled={loading || !newNumber}
                  size="icon"
                  className="shrink-0"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Existing Numbers List */}
            {whatsappNumbers.length > 0 ? (
              <div className="space-y-2">
                {whatsappNumbers.map((num) => (
                  <div 
                    key={num.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      num.isPrimary ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <WhatsAppIcon className={`h-5 w-5 ${num.isPrimary ? 'text-green-600' : 'text-muted-foreground'}`} size={20} />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">+234{num.number}</span>
                          {num.isPrimary && (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                              Primary
                            </Badge>
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground">{num.label}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!num.isPrimary && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetPrimary(num.id)}
                          disabled={loading}
                          title="Set as primary"
                        >
                          <Star className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveNumber(num.id)}
                        disabled={loading}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Phone className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No WhatsApp numbers added yet</p>
                <p className="text-sm">Add a number above to start receiving customer messages</p>
              </div>
            )}

            {/* Business Name */}
            <div className="pt-4 border-t">
              <Label htmlFor="business">Business Name</Label>
              <Input
                id="business"
                placeholder="Your Business Name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className="mt-2"
              />
              <Button 
                onClick={async () => {
                  await saveNumbers(whatsappNumbers);
                  toast.success("Business name saved");
                }}
                variant="outline"
                size="sm"
                className="mt-2"
                disabled={loading}
              >
                Save Business Name
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle>WhatsApp Features</CardTitle>
            <CardDescription>Configure how your business works with WhatsApp</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-reply">Auto-Reply Messages</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically respond to customer inquiries
                </p>
              </div>
              <Switch
                id="auto-reply"
                checked={autoReply}
                onCheckedChange={setAutoReply}
                disabled={!isConnected}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="catalog-sync">Catalog Sync</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically sync products to WhatsApp catalog
                </p>
              </div>
              <Switch
                id="catalog-sync"
                checked={catalogSync}
                onCheckedChange={setCatalogSync}
                disabled={!isConnected}
              />
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <div className="flex gap-2 items-start">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm">
                    <strong>Pro Tip:</strong> Use your storefront link when sharing on WhatsApp Status - 
                    it shows your product images as a rich preview!
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
